import { Button, Form, FormControl, FormGroup, FormLabel } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Overlay } from "../elements/Overlay";
import { handleApiError, safeJsonParse } from "../../utils/api";
import { obscureEmail } from "../../utils/utils";
import { SharePermission } from "../interfaces/Task";
import { GroupMemberDisplay } from "../interfaces/UsersGroup";

export interface UsersGroupAddProps {
    submitCallback: CallableFunction;
    closeCallback: CallableFunction;
}

export default function UsersGroupAdd(props: UsersGroupAddProps) {
    const { t } = useTranslation();

    const navigate = useNavigate();

    const userId = localStorage.getItem('userId');

    const [usersFound, setUsersFound] = useState<any[]>([]);
    const [membersSelected, setMembersSelected] = useState<GroupMemberDisplay[]>([]);

    const handleClose = () => {
        props.closeCallback();
    }

    const handleUsersSearch = (event: any) => {
        if (event.target.value.length < 3) {
            setUsersFound([]);
        } else {
            let reqUrl = import.meta.env.VITE_API_URL + '/v1/user/search';
            reqUrl += '?search=' + event.target.value;
            const requestOptions = {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + localStorage.getItem('token')
                }
            };
            fetch(reqUrl, requestOptions)
                .then(response => {
                    if (handleApiError(response, navigate)) {
                        return null;
                    }
                    return safeJsonParse(response);
                })
                .then(data => {
                    if (!data) return;
                    const newUsersFound = data.users.map((u: any) => ({
                        id: u.id,
                        email: u.email,
                        name: u.name || '',
                        label: obscureEmail(u.email) + (u.name ? ' (' + u.name + ')' : '')
                    }));
                    setUsersFound(newUsersFound);
                });
        }
    }

    const handleSelectUser = (event: any) => {
        const id = event.target.id.replace('userFound-', '');
        const selectedUser = usersFound.find((u: any) => u.id === id);
        if (selectedUser && !membersSelected.some(m => m.id === id)) {
            setMembersSelected([
                ...membersSelected,
                {
                    ...selectedUser,
                    permission: 'view' as SharePermission
                }
            ]);
        }
    }

    const handleUnselectUser = (event: any) => {
        const id = event.target.id.replace('userSelected-', '');
        setMembersSelected(membersSelected.filter(m => m.id !== id));
    }

    const handlePermissionChange = (userId: string, permission: SharePermission) => {
        setMembersSelected(membersSelected.map(m =>
            m.id === userId ? { ...m, permission } : m
        ));
    }

    const handleSubmit = (event: any) => {
        event.preventDefault();

        const newUsersGroup = {
            name: event.target.name.value,
            description: event.target.description.value,
            userId: userId,
            members: membersSelected.map(m => ({
                user: m.id,
                permission: m.permission
            }))
        };

        const requestOptionsPost = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + localStorage.getItem('token')
            },
            body: JSON.stringify(newUsersGroup)
        };
        fetch(import.meta.env.VITE_API_URL + '/v1/users-groups', requestOptionsPost)
            .then(response => {
                if (handleApiError(response, navigate)) {
                    return null;
                }
                return safeJsonParse(response);
            })
            .then(data => {
                if (!data) return;
                props.submitCallback(data);
            });
    }

    return(
        <Overlay
            title={ t("Add new group") }
            onClose={ handleClose }
            className="usersgroup-add-container"
        >
            <div className="usersgroup-form-container">
                <Form onSubmit={ handleSubmit }>
                    <FormGroup className="name mb-3" controlId="name">
                        <Form.Label aria-required={ true }>{ t("Name") }</Form.Label>
                        <Form.Control type="text" required={ true } name="name"
                            placeholder={ t("Enter a name") } />
                    </FormGroup>
                    <Form.Group className="field field-description mb-3">
                        <FormLabel htmlFor="description">{ t("Description") }</FormLabel>
                        <FormControl as="textarea" name="description" />
                    </Form.Group>

                    <h5>{ t("Members") }</h5>
                    <p className="text-muted small">
                        { t("Add members to the group and set their permissions. These permissions will apply when sharing tasks with this group.") }
                    </p>

                    <div className="members-selector">
                        <div className="search mb-3">
                            <Form.Control
                                type="text"
                                placeholder={ t("Search by email or name...") }
                                onChange={ handleUsersSearch }
                            />
                        </div>

                        { usersFound.length > 0 &&
                            <div className="users-found mb-3">
                                <div className="label mb-2">
                                    <strong>{ t("Click to add members:") }</strong>
                                </div>
                                <ul className="list-unstyled">
                                    { usersFound.filter(f => !membersSelected.some(m => m.id === f.id)).map(u => (
                                        <li key={ `userFound-${u.id}` } className="d-flex align-items-center mb-1">
                                            <span>{ u.label }</span>
                                            <a
                                                className="select-user ms-2"
                                                onClick={ handleSelectUser }
                                                id={ `userFound-${u.id}` }
                                            ></a>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        }

                        { membersSelected.length > 0 &&
                            <div className="members-selected">
                                <div className="label mb-2">
                                    <strong>{ t("Group members:") }</strong>
                                </div>
                                <ul className="list-unstyled">
                                    { membersSelected.map(m => (
                                        <li key={ `userSelected-${m.id}` } className="d-flex align-items-center justify-content-between mb-2 pb-2 border-bottom">
                                            <div className="d-flex align-items-center">
                                                <span>{ m.label }</span>
                                                <a
                                                    className="unselect-user ms-2"
                                                    onClick={ handleUnselectUser }
                                                    id={ `userSelected-${m.id}` }
                                                ></a>
                                            </div>
                                            <Form.Select
                                                size="sm"
                                                value={ m.permission }
                                                onChange={(e) => handlePermissionChange(m.id, e.target.value as SharePermission)}
                                                style={{ width: 'auto', minWidth: '120px' }}
                                            >
                                                <option value="view">{ t("Can view") }</option>
                                                <option value="edit">{ t("Can edit") }</option>
                                            </Form.Select>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        }
                    </div>

                    <div className="actions mt-4">
                        <Button type="submit" variant="primary">{ t("Save") }</Button>
                    </div>
                </Form>
            </div>
        </Overlay>
    );
}
